/**
 * Cart promo progress engine (free shipping + order threshold + collection promos).
 * Amounts are in major currency units (e.g. dollars), not cents.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.GeekoCartPromoEngine = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DEFAULT_CONFIG = {
    freeShippingThreshold: 69,
    thresholdShowFrom: 69,
    tiers: [
      { amount: 99, off: 10 },
      { amount: 129, off: 15 },
    ],
    b3g1: {
      id: 'b3g1',
      triggerQty: 4,
      title: 'Buy 3 Get 1 Free',
    },
    multi: {
      id: 'multi',
      tiers: [
        { qty: 2, rate: 0.1 },
        { qty: 3, rate: 0.15 },
      ],
      title: '2 For 10% Off · 3 For 15% Off',
    },
  };

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function roundMoney(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
  }

  function mergeConfig(overrides) {
    var cfg = Object.assign({}, DEFAULT_CONFIG, overrides || {});
    cfg.tiers = (overrides && overrides.tiers) || DEFAULT_CONFIG.tiers;
    cfg.b3g1 = Object.assign({}, DEFAULT_CONFIG.b3g1, (overrides && overrides.b3g1) || {});
    cfg.multi = Object.assign({}, DEFAULT_CONFIG.multi, (overrides && overrides.multi) || {});
    cfg.multi.tiers = (overrides && overrides.multi && overrides.multi.tiers) || DEFAULT_CONFIG.multi.tiers;
    return cfg;
  }

  function thresholdSavings(orderAmount, cfg) {
    var amount = roundMoney(orderAmount);
    var best = 0;
    var tierIndex = -1;
    for (var i = 0; i < cfg.tiers.length; i++) {
      if (amount >= cfg.tiers[i].amount) {
        best = cfg.tiers[i].off;
        tierIndex = i;
      }
    }
    return { savings: best, tierIndex: tierIndex };
  }

  function collectGroup(lines, groupId) {
    var qty = 0;
    var subtotal = 0;
    var unitPrices = [];
    var groupLines = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (line.group !== groupId) continue;
      var q = Number(line.quantity) || 0;
      var price = roundMoney(line.price);
      var finalPrice = line.finalPrice != null ? roundMoney(line.finalPrice) : price;
      qty += q;
      subtotal = roundMoney(subtotal + price * q);
      groupLines.push({
        key: line.key,
        quantity: q,
        price: price,
        finalPrice: finalPrice,
      });
      for (var j = 0; j < q; j++) {
        unitPrices.push({ key: line.key, price: price, finalPrice: finalPrice });
      }
    }
    unitPrices.sort(function (a, b) {
      if (a.price !== b.price) return a.price - b.price;
      // Prefer already-free Shopify lines, then smaller qty gift splits.
      if (a.finalPrice !== b.finalPrice) return a.finalPrice - b.finalPrice;
      return 0;
    });
    return { qty: qty, subtotal: subtotal, unitPrices: unitPrices, lines: groupLines };
  }

  function pickB3g1FreeLine(group) {
    var lines = group.lines || [];
    if (!lines.length) return null;

    // Prefer the line Shopify already zeroed (typical Buy X Get Y split line).
    var applied = lines
      .filter(function (l) {
        return l.finalPrice <= 0.001 && l.quantity > 0;
      })
      .sort(function (a, b) {
        return a.quantity - b.quantity;
      });
    if (applied.length) return applied[0];

    // Fallback: cheapest original unit; on ties prefer qty=1 gift-style lines.
    var cheapest = roundMoney(group.unitPrices[0].price);
    var candidates = lines.filter(function (l) {
      return roundMoney(l.price) === cheapest;
    });
    candidates.sort(function (a, b) {
      if (a.quantity !== b.quantity) return a.quantity - b.quantity;
      return a.finalPrice - b.finalPrice;
    });
    return candidates[0] || null;
  }

  function b3g1Savings(group) {
    if (group.qty < 4 || !group.unitPrices.length) {
      return { savings: 0, freeKey: null, freePrice: 0 };
    }
    var freeUnit = group.unitPrices[0];
    var freeLine = pickB3g1FreeLine(group);
    return {
      savings: roundMoney(freeUnit.price),
      freeKey: freeLine ? freeLine.key : freeUnit.key,
      freePrice: roundMoney(freeUnit.price),
    };
  }

  function multiSavings(group, cfg) {
    var rate = 0;
    var appliedQty = 0;
    for (var i = 0; i < cfg.multi.tiers.length; i++) {
      var t = cfg.multi.tiers[i];
      if (group.qty >= t.qty) {
        rate = t.rate;
        appliedQty = t.qty;
      }
    }
    if (!rate) return { savings: 0, rate: 0, appliedQty: 0 };
    return {
      savings: roundMoney(group.subtotal * rate),
      rate: rate,
      appliedQty: appliedQty,
    };
  }

  function pickWinner(threshold, b3g1, multi) {
    var candidates = [];
    if (threshold.savings > 0) {
      candidates.push({ type: 'threshold', savings: threshold.savings, tierIndex: threshold.tierIndex });
    }
    if (b3g1.savings > 0) {
      candidates.push({ type: 'b3g1', savings: b3g1.savings });
    }
    if (multi.savings > 0) {
      candidates.push({ type: 'multi', savings: multi.savings, rate: multi.rate });
    }
    if (!candidates.length) return { type: 'none', savings: 0 };

    candidates.sort(function (a, b) {
      if (b.savings !== a.savings) return b.savings - a.savings;
      // Tie-break: collection strictly preferred only when savings are equal? Plan scene 9:
      // threshold wins when threshold >= collection. So on equal savings, threshold wins.
      var rank = { threshold: 0, b3g1: 1, multi: 1 };
      return rank[a.type] - rank[b.type];
    });
    return candidates[0];
  }

  function freeShippingState(orderAmount, cfg) {
    var amount = roundMoney(orderAmount);
    var unlocked = amount >= cfg.freeShippingThreshold;
    return {
      visible: unlocked,
      unlocked: unlocked,
      progress: unlocked ? 1 : clamp(amount / cfg.freeShippingThreshold, 0, 1),
      remain: unlocked ? 0 : roundMoney(cfg.freeShippingThreshold - amount),
    };
  }

  function thresholdBarState(orderAmount, winner, cfg) {
    var amount = roundMoney(orderAmount);
    if (winner.type === 'b3g1' || winner.type === 'multi') {
      return { visible: false, mode: 'hidden', remain: 0, progress: 0, showAdd: false, unlockedOff: 0, nextOff: 0 };
    }
    if (amount < cfg.thresholdShowFrom) {
      return { visible: false, mode: 'hidden', remain: 0, progress: 0, showAdd: false, unlockedOff: 0, nextOff: 0 };
    }

    var t1 = cfg.tiers[0];
    var t2 = cfg.tiers[1];

    if (amount < t1.amount) {
      return {
        visible: true,
        mode: 'to_t1',
        remain: roundMoney(t1.amount - amount),
        progress: clamp(amount / t1.amount, 0, 1),
        showAdd: true,
        unlockedOff: 0,
        nextOff: t1.off,
      };
    }
    if (amount < t2.amount) {
      return {
        visible: true,
        mode: 'unlocked_t1',
        remain: roundMoney(t2.amount - amount),
        progress: clamp(amount / t2.amount, 0, 1),
        showAdd: true,
        unlockedOff: t1.off,
        nextOff: t2.off,
      };
    }
    return {
      visible: true,
      mode: 'unlocked_t2',
      remain: 0,
      progress: 1,
      showAdd: false,
      unlockedOff: t2.off,
      nextOff: 0,
    };
  }

  function isB3g1Applied(group) {
    return (group.lines || []).some(function (l) {
      return l.finalPrice <= 0.001 && l.quantity > 0;
    });
  }

  function isMultiApplied(group) {
    var lines = group.lines || [];
    var original = 0;
    var finalTotal = 0;
    for (var i = 0; i < lines.length; i++) {
      original = roundMoney(original + lines[i].price * lines[i].quantity);
      finalTotal = roundMoney(finalTotal + lines[i].finalPrice * lines[i].quantity);
    }
    return finalTotal < original - 0.01;
  }

  // Gray only when the collection discount did NOT take effect.
  // Collections can stack with each other; only 满减挤掉未生效时才 superseded.
  function isCollectionEffective(applied, savings, winner) {
    if (savings <= 0) return false;
    if (applied) return true;
    return winner.type !== 'threshold';
  }

  function b3g1GroupState(group, b3g1, winner, cfg) {
    if (group.qty <= 0) {
      return { id: 'b3g1', visible: false, banner: 'none', messageKey: '', messageParams: {}, showAdd: false, title: cfg.b3g1.title };
    }
    var need = cfg.b3g1.triggerQty;
    var more = Math.max(need - group.qty, 0);
    var applied = isB3g1Applied(group);
    if (isCollectionEffective(applied, b3g1.savings, winner)) {
      return {
        id: 'b3g1',
        visible: true,
        banner: 'success',
        messageKey: 'b3g1_success',
        messageParams: {},
        showAdd: false,
        title: cfg.b3g1.title,
      };
    }
    if (b3g1.savings > 0) {
      return {
        id: 'b3g1',
        visible: true,
        banner: 'superseded',
        messageKey: 'superseded',
        messageParams: {},
        showAdd: true,
        title: cfg.b3g1.title,
      };
    }
    return {
      id: 'b3g1',
      visible: true,
      banner: 'guide',
      messageKey: more === 1 ? 'b3g1_guide_one' : 'b3g1_guide_other',
      messageParams: { count: more },
      showAdd: true,
      title: cfg.b3g1.title,
    };
  }

  function multiGroupState(group, multi, winner, cfg) {
    if (group.qty <= 0) {
      return { id: 'multi', visible: false, banner: 'none', messageKey: '', messageParams: {}, showAdd: false, title: cfg.multi.title };
    }
    var applied = isMultiApplied(group);
    if (isCollectionEffective(applied, multi.savings, winner)) {
      var pct = Math.round((multi.rate || 0) * 100);
      if (group.qty >= 3) {
        return {
          id: 'multi',
          visible: true,
          banner: 'success',
          messageKey: 'multi_success_15',
          messageParams: { percent: pct },
          showAdd: false,
          title: cfg.multi.title,
        };
      }
      return {
        id: 'multi',
        visible: true,
        banner: 'guide',
        messageKey: 'multi_guide_15',
        messageParams: { count: 1 },
        showAdd: true,
        title: cfg.multi.title,
      };
    }
    if (multi.savings > 0) {
      return {
        id: 'multi',
        visible: true,
        banner: 'superseded',
        messageKey: 'superseded',
        messageParams: {},
        showAdd: true,
        title: cfg.multi.title,
      };
    }
    if (group.qty >= 2) {
      return {
        id: 'multi',
        visible: true,
        banner: 'guide',
        messageKey: 'multi_guide_15',
        messageParams: { count: 1 },
        showAdd: true,
        title: cfg.multi.title,
      };
    }
    return {
      id: 'multi',
      visible: true,
      banner: 'guide',
      messageKey: 'multi_guide_10',
      messageParams: { count: 1 },
      showAdd: true,
      title: cfg.multi.title,
    };
  }

  /**
   * @param {{ orderAmount: number, lines: Array<{key:string, quantity:number, price:number, group:'b3g1'|'multi'|'normal'}> }} input
   * @param {object} [configOverrides]
   */
  function compute(input, configOverrides) {
    var cfg = mergeConfig(configOverrides);
    var orderAmount = roundMoney(input && input.orderAmount);
    var lines = (input && input.lines) || [];

    var b3g1Group = collectGroup(lines, 'b3g1');
    var multiGroup = collectGroup(lines, 'multi');

    var threshold = thresholdSavings(orderAmount, cfg);
    var b3g1 = b3g1Savings(b3g1Group);
    var multi = multiSavings(multiGroup, cfg);
    var winner = pickWinner(threshold, b3g1, multi);

    var b3g1Applied = isB3g1Applied(b3g1Group);
    var freeLineKeys = [];
    if (b3g1.freeKey && (b3g1Applied || winner.type === 'b3g1')) {
      freeLineKeys.push(b3g1.freeKey);
    }

    return {
      orderAmount: orderAmount,
      config: cfg,
      threshold: threshold,
      b3g1: Object.assign({}, b3g1, { qty: b3g1Group.qty, subtotal: b3g1Group.subtotal, applied: b3g1Applied }),
      multi: Object.assign({}, multi, {
        qty: multiGroup.qty,
        subtotal: multiGroup.subtotal,
        applied: isMultiApplied(multiGroup),
      }),
      winner: winner.type,
      winnerMeta: winner,
      freeShipping: freeShippingState(orderAmount, cfg),
      thresholdBar: thresholdBarState(orderAmount, winner, cfg),
      groups: [
        b3g1GroupState(b3g1Group, b3g1, winner, cfg),
        multiGroupState(multiGroup, multi, winner, cfg),
      ],
      freeLineKeys: freeLineKeys,
    };
  }

  return {
    DEFAULT_CONFIG: DEFAULT_CONFIG,
    compute: compute,
    mergeConfig: mergeConfig,
    roundMoney: roundMoney,
  };
});
